
import * as React from "react"
import * as Toggle from "@radix-ui/react-toggle"
import { cn } from "@/lib/utils"
import { useSidebar } from "./sidebar-provider"
import { ChevronLeft, Menu } from "lucide-react"

const ToggleRoot = Toggle.Root

interface SidebarTriggerProps extends React.ComponentPropsWithoutRef<typeof ToggleRoot> {
  asChild?: boolean
  showIcon?: boolean
}

function ensureSidebarClickable() {
  // This function will be called when sidebar components mount to ensure they're clickable on mobile
  if (typeof window !== "undefined") {
    const style = document.createElement('style');
    style.innerHTML = `
      @media (max-width: 768px) {
        [data-sidebar-trigger="true"] {
          pointer-events: auto !important;
          touch-action: auto !important;
          cursor: pointer !important;
          z-index: 50 !important;
          position: relative !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof ToggleRoot>,
  SidebarTriggerProps
>(({ className, asChild = false, showIcon = true, children, ...props }, ref) => {
  const { isCollapsed, toggleCollapsed } = useSidebar();
  
  React.useEffect(() => {
    ensureSidebarClickable();
  }, []);
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    toggleCollapsed();
    if (props.onClick) {
      props.onClick(e);
    }
  };
  
  return (
    <ToggleRoot
      data-sidebar-trigger="true"
      ref={ref}
      className={cn("flex items-center justify-center hover:bg-accent rounded-md", className)}
      style={{
        pointerEvents: 'auto',
        touchAction: 'auto',
        cursor: 'pointer',
        zIndex: 50,
        position: 'relative'
      }}
      onClick={handleClick}
      {...props}
    >
      {showIcon ? (
        isCollapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
      ) : null}
      {children}
    </ToggleRoot>
  );
});
SidebarTrigger.displayName = ToggleRoot.displayName;

export { SidebarTrigger }
